// src/AdminPages/AdminLayout/AdminNavbar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, NavLink, useNavigate } from 'react-router-dom';
import './AdminNavbar.css';
import logo from '../../assets/logo.png'; // Nutrivio Logo path
import axiosInstance from '../../api/axiosInstance';
import AdminFooter from '../AdminFooter/AdminFooter';

// --- Icons ---
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
);

const LessThanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);

const ChevronIcon = ({ isOpen }) => (
  <svg className={`admin-navbar-side-bar-chevron ${isOpen ? 'rotated' : ''}`} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
);

const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px' }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
);

// --- Nutrivio Admin Menu Configuration ---
const menuItems = [
  { title: "Dashboard", path: "/admin/dashboard" },
  { title: "Institutional Enquiries", path: "/admin/institutional-enquiries" },
  { title: "Faculty Registrations", path: "/admin/faculty-registrations" },
  { title: "Gallery", path: "/admin/add-gallery" },
  { title: "Contact Enquiries", path: "/admin/view-contact" },
];

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [adminDetails, setAdminDetails] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1024);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openFolders, setOpenFolders] = useState({});

  // LOGOUT Function
  const handleLogout = async () => {
    try {
      const refreshToken = sessionStorage.getItem('adminRefreshToken');
      await axiosInstance.post('/api/admin/logout', { refreshToken });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminRefreshToken');
      window.location.href = '/admin';
    }
  };

  // Auto-expand folder containing active child route
  useEffect(() => {
    const activeFolder = {};
    menuItems.forEach((item) => {
      if (item.items) {
        const hasActiveChild = item.items.some(child => child.path === location.pathname);
        if (hasActiveChild) {
          activeFolder[item.title] = true;
        }
      }
    });
    setOpenFolders((prev) => ({ ...prev, ...activeFolder }));
  }, [location.pathname]);

  // Token Verification
  useEffect(() => {
    const verifyToken = async () => {
      const token = sessionStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin', { replace: true });
        return;
      }

      try {
        const res = await axiosInstance.get('/api/admin/me');
        setAdminDetails(res.data.admin);
      } catch (error) {
        if (error.response?.status === 401) {
          sessionStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminRefreshToken');
          navigate('/admin', { replace: true });
        } else {
          console.error("Token verification failed (Server/Network Error).");
        }
      }
    };
    verifyToken();
  }, [navigate]);

  // Handle Resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle Click Outside user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleFolder = (folderTitle) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderTitle]: !prev[folderTitle]
    }));
  };

  return (
    <div className={`admin-navbar-side-bar-layout ${isSidebarOpen ? 'sidebar-visible' : 'sidebar-hidden'}`}>

      {/* 1. SIDEBAR PANEL */}
      <aside className="admin-navbar-side-bar-panel">
        <div className="admin-navbar-side-bar-side-header">
          <Link to="/admin/dashboard" className="admin-navbar-brand-link">
            <img src={logo} alt="Nutrivio Medical Services" className="admin-navbar-sidebar-logo" />
          </Link>
          <button 
            type="button" 
            className="admin-navbar-side-bar-close-btn" 
            onClick={toggleSidebar}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>
        
        <div className="admin-navbar-side-bar-scroll-area">
          <div className="admin-navbar-menu-category">ADMIN NAVIGATION</div>
          {menuItems.map((item) => {
            if (item.items) {
              const isOpen = !!openFolders[item.title];
              const isChildActive = item.items.some(child => child.path === location.pathname);

              return (
                <div key={item.title} className="admin-navbar-side-bar-folder-container">
                  <button 
                    onClick={() => toggleFolder(item.title)}
                    className={`admin-navbar-side-bar-folder-trigger ${isOpen ? 'open' : ''} ${isChildActive ? 'parent-active' : ''}`}
                  >
                    <span className="admin-navbar-side-bar-folder-label">
                      <FolderIcon />
                      {item.title}
                    </span>
                    <ChevronIcon isOpen={isOpen} />
                  </button>
                  
                  <div className={`admin-navbar-side-bar-submenu ${isOpen ? 'open' : ''}`}>
                    {item.items.map((subItem) => (
                      <NavLink 
                        key={subItem.path} 
                        to={subItem.path} 
                        className={({ isActive }) => `admin-navbar-side-bar-submenu-link ${isActive ? 'active' : ''}`}
                        onClick={() => { if(window.innerWidth <= 1024) setIsSidebarOpen(false); }}
                      >
                        <span className="admin-navbar-bullet">•</span>
                        {subItem.title}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <NavLink 
                key={item.path} 
                to={item.path} 
                className={({ isActive }) => `admin-navbar-side-bar-link-item ${isActive ? 'active' : ''}`}
                onClick={() => { if(window.innerWidth <= 1024) setIsSidebarOpen(false); }}
              >
                <span>{item.title}</span>
              </NavLink>
            );
          })}
        </div>

        <div className="admin-navbar-side-bar-side-footer">
          <button onClick={handleLogout} className="admin-navbar-side-bar-logout-action">
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* 2. VIEWPORT MAIN */}
      <div className="admin-navbar-side-bar-main-viewport">
        <nav className="admin-navbar-side-bar-top-header">
          <div className="admin-navbar-side-bar-header-left">
            <button className="admin-navbar-side-bar-menu-toggle" onClick={toggleSidebar} aria-label="Toggle Sidebar">
              {isSidebarOpen ? <LessThanIcon /> : <MenuIcon />}
            </button>
            
            <Link to="/admin/dashboard" className="admin-navbar-side-bar-logo">
              <span className="admin-navbar-side-bar-logo-text">NUTRIVIO</span>
              <span className="admin-navbar-portal-tag">Admin Portal</span>
            </Link>
          </div>

          <div className="admin-navbar-side-bar-header-right" ref={dropdownRef}>
            <div className="admin-navbar-side-bar-header-user-text">
              <span className="admin-navbar-side-bar-header-name">{adminDetails?.name || adminDetails?.email || 'Administrator'}</span>
              <span className="admin-navbar-side-bar-header-role">{adminDetails?.role || 'Admin Access'}</span>
            </div>
            
            <button 
              className={`admin-navbar-side-bar-profile-circle ${isUserMenuOpen ? 'active' : ''}`} 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              aria-label="Toggle User Menu"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="true"
            >
              <UserIcon />
            </button>

            {isUserMenuOpen && (
              <div className="admin-navbar-side-bar-header-dropdown-box">
                <div className="admin-navbar-side-bar-drop-user-info">
                  <p className="admin-navbar-side-bar-drop-name">{adminDetails?.name || 'Admin'}</p>
                  <p className="admin-navbar-side-bar-drop-email">{adminDetails?.email}</p>
                </div>
                <div className="admin-navbar-side-bar-drop-divider"></div>
                <Link to="/admin/change-password" className="admin-navbar-side-bar-drop-link" onClick={() => setIsUserMenuOpen(false)}>
                  Change Password
                </Link>
                <button onClick={handleLogout} className="admin-navbar-side-bar-drop-link logout-red">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </nav>

        <main className="admin-navbar-side-bar-page-wrapper">
          <div className="admin-navbar-side-bar-content-container">
            <Outlet />
          </div>
          <AdminFooter />
        </main>

        <div className={`admin-navbar-side-bar-mobile-curtain ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>
      </div>
    </div>
  );
};

export default AdminNavbar;